import { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { ConfirmDialog } from "primereact/confirmdialog";

import TopicRuleUpdate from "./TopicRuleUpdate";
import { ITopicRule, ITopicRuleListProps } from "./TopicRule.model";
import { topicRuleService } from "../../service/TopicRule/topicRule.service";
import { Can } from "../../ability/can";
import {
  PERMISSIONS_ACTION,
  PERMISSIONS_MODEL,
} from "../../library/utilities/constants/permissions";

const TopicRuleList = ({
  openNew,
  loadTopicRules,
  data,
  loading,
}: ITopicRuleListProps) => {
  const [topicRuleDialog, setTopicRuleDialog] = useState(false);
  const [selectedTopicRule, setSelectedTopicRule] = useState<ITopicRule | null>(
    null
  );
  const [confirmDialog, setConfirmDialog] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const deleteTopicRule = (id: number) => {
    setDeleteId(id);
    setConfirmDialog(true);
  };

  const confirmDeleteTopicRule = async () => {
    if (!deleteId) return;
    await topicRuleService.remove(deleteId);

    setConfirmDialog(false);
    loadTopicRules();
  };

  const actionBodyTemplate = (row: ITopicRule) => {
    return (
      <div className="flex gap-2">
        <Can I={PERMISSIONS_ACTION.TOPIC_RULE_CHANGE} a={PERMISSIONS_MODEL.TOPIC_RULE}>
          <Button
            label="Edit"
            icon="pi pi-pencil"
            onClick={() => {
              setSelectedTopicRule(row);
              setTopicRuleDialog(true);
            }}
          />
        </Can>

        <Can I={PERMISSIONS_ACTION.TOPIC_RULE_DELETE} a={PERMISSIONS_MODEL.TOPIC_RULE}>
          <Button
            label="Delete"
            icon="pi pi-trash"
            severity="danger"
            onClick={() => deleteTopicRule(row.id)}
          />
        </Can>
      </div>
    );
  };

  const topicRuleColumns = [
  
    {
      field: "topic_name",
      header: "Topic",
    },
    {
      field: "year_of_experience",
      header: "Years of Experience",
    },
    {
      field: "high_no_of_question",
      header: "High Questions",
    },
    {
      field: "medium_no_of_question",
      header: "Medium Questions",
    },
    {
      field: "low_no_of_question",
      header: "Low Questions",
    },
  ];

  const [globalFilter, setGlobalFilter] = useState("");
  const filteredData = data.filter((topicRule: ITopicRule) => {
    const search = globalFilter.toLowerCase();
    return topicRule?.topic_name?.toLowerCase().includes(search);
  });

  return (
    <>
      <Dialog
        visible={topicRuleDialog}
        header="Update Topic Rule"
        modal
        onHide={() => setTopicRuleDialog(false)}
        className="w-5">
        {selectedTopicRule && (
          <TopicRuleUpdate
            data={selectedTopicRule}
            setTopicRuleDialog={setTopicRuleDialog}
            loadTopicRules={loadTopicRules}
          />
        )}
      </Dialog>

      {/* Topic Rule Table */}
      <DataTable
        value={filteredData}
        loading={loading}
        dataKey="id"
        paginator
        rows={10}
        header={
          <div className="flex justify-content-between align-items-center gap-3">
            <h3>Topic Rule Management</h3>
            <div className="flex">
              <span className="p-input-icon-left mr-3">
                <i className="pi pi-search" />
                <input
                  type="text"
                  value={globalFilter}
                  onChange={(e) => setGlobalFilter(e.target.value)}
                  placeholder="Search by topic"
                  className="p-inputtext p-component"
                />
              </span>
              <Can I={PERMISSIONS_ACTION.TOPIC_RULE_ADD} a={PERMISSIONS_MODEL.TOPIC_RULE}>
                <Button label="New Topic Rule" onClick={openNew} />
              </Can>
            </div>
          </div>
        }>
        {topicRuleColumns.map((col) => (
          <Column
            sortable
            key={col.field}
            field={col.field}
            header={col.header}
          
          />
        ))}

        <Column header="Actions" body={actionBodyTemplate} />
      </DataTable>

      {/* Delete Confirmation */}
      <ConfirmDialog
        visible={confirmDialog}
        onHide={() => setConfirmDialog(false)}
        message="Are you sure you want to delete this topic rule?"
        header="Delete Confirmation"
        icon="pi pi-exclamation-triangle"
        accept={confirmDeleteTopicRule}
        reject={() => setConfirmDialog(false)}
      />
    </>
  );
};

export default TopicRuleList;
